"use client";

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  OpenAccountContextType,
  OpenAccountUser,
  UserActivationConfirmation,
} from "@/types/openAccount";
import { RecaptchaResponse } from "@/types/recaptcha";
import { OpenAccountValidation, ValidationError } from "@/types/validation";

import { httpRequest } from "@/lib/http";

import { useRecaptcha } from "@/context/util/recaptcha";
import { useSecuritySettings } from "@/context/util/securitySettings";
import { useUi } from "@/context/util/ui";

import openAccountAlerts from "@/components/alerts/openAccount";
import CountdownTimer from "@/components/countdown";

const OpenAccountContext = createContext<OpenAccountContextType>(null!);

export function OpenAccountProvider({ children }: { children: ReactNode }) {
  const {
    handleSetAlert,
    needsBackgroundColorUpdate,
    setAlerts,
    setBackgroundColor,
    setLoading,
  } = useUi();
  const { recaptchaRequest } = useRecaptcha();
  const { timer, timerSpan } = useSecuritySettings();

  const [activeStep, setActiveStep] = useState<number>(-1);
  const [errors, setErrors] = useState<ValidationError<OpenAccountValidation>>(
    {}
  );
  const [openAccountUser, setOpenAccountUser] =
    useState<OpenAccountUser | null>(null);

  useEffect(() => {
    const newBackgroundColor = "#f8f8f8";
    if (needsBackgroundColorUpdate(newBackgroundColor))
      setBackgroundColor(newBackgroundColor);
  }, [needsBackgroundColorUpdate, setBackgroundColor]);

  useEffect(() => {
    setAlerts(openAccountAlerts.map);
  }, []);

  const getAccountSetup = useCallback(async () => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      const { firstName, lastName, birthdate, zip } = openAccountUser ?? {};

      try {
        const url = `/account-utilities/api/open/lookup`;

        const body = JSON.stringify({ firstName, lastName, birthdate, zip });
        const headers = { ...(init?.headers ?? {}) };
        const method = "POST";

        const requestInit: RequestInit = { body, headers, method };

        const openAccountData = (
          await httpRequest<OpenAccountUser | RecaptchaResponse>(
            url,
            requestInit
          )
        ).data;

        if (!openAccountData) throw new Error("Empty http response");

        if ("valid" in openAccountData) {
          handleSetAlert([0, 1], openAccountAlerts.getRecaptchaAlert());
          return openAccountData;
        }

        setOpenAccountUser((prev) => ({ ...(prev ?? {}), ...openAccountData }));

        if (openAccountData.needsSetup === false)
          handleSetAlert(
            [0, 1],
            openAccountAlerts.getAccountPreviouslySetupAlert()
          );
        else {
          timer.current = (
            <CountdownTimer<OpenAccountUser>
              fields={["token", "verified"]}
              ref={timerSpan}
              setExpired={setOpenAccountUser}
              targetDate={openAccountData.tokenExpiration}
            />
          );

          handleSetAlert([0], openAccountAlerts.getTermsAlert());

          handleSetAlert(
            [1],
            openAccountAlerts.getLookupSuccessAlert(
              openAccountData.userIndicator,
              timer.current
            )
          );
        }
      } catch (error) {
        const message =
          "Failed to get account information. Please try again later.";
        console.error(message, error);

        handleSetAlert(
          [0, 1],
          openAccountAlerts.getGeneralErrorAlert(message, error, "EM")
        );
      } finally {
        setLoading(false);
      }
      // if status === 0 => rate limited
    };

    return recaptchaRequest("open_account_validation", request);
  }, [openAccountUser, recaptchaRequest]);

  // advances step to 3
  const setupAccount = useCallback(async () => {
    const request = async (init?: RequestInit) => {
      window.scrollTo(0, 0);
      setLoading(true);

      // figure out why setPasswordPinSecretAnswer is failing
      try {
        const url = `/account-utilities/api/open/setup`;

        const body = JSON.stringify(openAccountUser);
        const headers = { ...(init?.headers ?? {}) };
        const method = "POST";

        const requestInit: RequestInit = { body, headers, method };

        const setupAccountData = (
          await httpRequest<UserActivationConfirmation | RecaptchaResponse>(
            url,
            requestInit
          )
        ).data;

        if (!setupAccountData) throw new Error("Empty http response");

        if ("valid" in setupAccountData) {
          handleSetAlert([2], openAccountAlerts.getRecaptchaAlert());
          return setupAccountData;
        }

        setOpenAccountUser(
          (prev) => ({ ...(prev ?? {}), needsSetup: false } as OpenAccountUser)
        );

        handleSetAlert(
          [3],
          openAccountAlerts.getSetupSuccessAlert(
            setupAccountData,
            openAccountUser?.userIndicator ?? "EM"
          )
        );

        setActiveStep(3);
      } catch (error) {
        const message = "Unable to open your account";
        console.error(message, error);

        handleSetAlert(
          [0, 1, 2],
          openAccountAlerts.getGeneralErrorAlert(
            message,
            error,
            openAccountUser?.userIndicator ?? "EM"
          )
        );
      } finally {
        setLoading(false);
      }
    };

    return recaptchaRequest("open_account_confirmation", request);
  }, [openAccountUser, recaptchaRequest]);

  return (
    <OpenAccountContext.Provider
      value={{
        activeStep,
        errors,
        getAccountSetup,
        openAccountUser,
        setActiveStep,
        setErrors,
        setOpenAccountUser,
        setupAccount,
      }}
    >
      {children}
    </OpenAccountContext.Provider>
  );
}

export function useOpenAccount() {
  const ctx = useContext(OpenAccountContext);
  if (!ctx)
    throw new Error("useOpenAccount must be used within a OpenAccountContext");
  return ctx;
}
